import { useCallback, useEffect, useState } from "react";
import { RefreshCw, XCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { subjectName } from "@/lib/entities";
import { inputClass } from "@/components/FieldInput";
import { Card, StatusBadge } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const LABEL = { sent: "Inviato", delivered: "Visualizzato", completed: "Firmato", declined: "Rifiutato", voided: "Annullato" };
const STATUS_MAP = { sent: "in_corso", delivered: "istituito", completed: "completato", declined: "errore", voided: "annullata" };

export function SendSignatureDialog({ doc, onClose }) {
  const [subject, setSubject] = useState(`Richiesta di firma: ${doc.title}`);
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([{ name: "", email: "", routing_order: 1, role: "" }]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!doc.trust_id) return;
    Promise.all([api.get("/e/trust_subjects", { params: { trust_id: doc.trust_id } }), api.get("/e/subjects")])
      .then(([l, s]) => setPeople(l.data.map((x) => ({ ...s.data.find((y) => y.id === x.subject_id), role: x.role })).filter((p) => p.email)))
      .catch(() => {});
  }, [doc.trust_id]);

  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const addPerson = (p) => setRows((r) => [...r.filter((x) => x.email), { name: subjectName(p), email: p.email, routing_order: r.filter((x) => x.email).length + 1, role: p.role }]);
  const send = async () => {
    const recipients = rows.filter((r) => r.name && r.email);
    if (!recipients.length) return toast.error("Aggiungi almeno un firmatario con nome ed email");
    setBusy(true);
    try {
      await api.post("/signatures", { document_id: doc.id, recipients, email_subject: subject, message });
      toast.success("Documento inviato in firma tramite DocuSign");
      onClose(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Invia in firma con DocuSign</DialogTitle>
          <DialogDescription>Al documento viene aggiunta una pagina delle firme. Firmatari con lo stesso ordine firmano in parallelo, altrimenti in sequenza.</DialogDescription>
        </DialogHeader>
        <input className={inputClass} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="signature-subject-input" />
        <textarea className={`${inputClass} h-20 py-2`} placeholder="Messaggio per i firmatari (facoltativo)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="signature-message-input" />
        {people.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="signature-people">
            {people.map((p) => <Button key={p.id + p.role} size="sm" variant="outline" onClick={() => addPerson(p)} data-testid={`signature-add-${p.id}`}><Plus className="h-3 w-3 mr-1" />{subjectName(p)} · {p.role}</Button>)}
          </div>
        )}
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_70px_36px] gap-2">
              <input className={inputClass} placeholder="Nome e cognome" value={r.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`signer-name-${i}`} />
              <input className={inputClass} placeholder="Email" value={r.email} onChange={(e) => set(i, "email", e.target.value)} data-testid={`signer-email-${i}`} />
              <input type="number" min="1" className={inputClass} title="Ordine di firma" value={r.routing_order} onChange={(e) => set(i, "routing_order", Number(e.target.value) || 1)} data-testid={`signer-order-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setRows([...rows, { name: "", email: "", routing_order: rows.length + 1, role: "" }])} data-testid="signer-add-row"><Plus className="h-4 w-4 mr-1" />Aggiungi firmatario</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Annulla</Button>
          <Button disabled={busy} onClick={send} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="signature-send-button">{busy ? "Invio…" : "Invia in firma"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SignaturesPanel({ trustId }) {
  const [rows, setRows] = useState([]);
  const [st, setSt] = useState(null);
  const load = useCallback(() => {
    api.get("/signatures", { params: { trust_id: trustId } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/signatures/status").then((r) => setSt(r.data)).catch(() => {});
  }, [trustId]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <Card data-testid="signatures-panel">
      <div className="p-4 border-b border-slate-100 text-xs text-slate-500" data-testid="signatures-config">
        {st && (st.configured ? `DocuSign collegato (ambiente ${st.environment}). Per inviare un documento usa l'icona penna in Documenti.` : "DocuSign non ancora configurato: servono le credenziali dell'account.")}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="px-4 py-3">Documento</th><th className="px-4 py-3">Firmatari</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Inviato</th><th className="px-4 py-3 text-right">Azioni</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nessuna richiesta di firma</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`signature-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.document_title}{r.signed_document_id && <p className="text-[11px] text-emerald-700">Firmato e certificato archiviati in Documenti</p>}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.recipients.map((x) => `${x.routing_order}. ${x.name}`).join(" · ")}</td>
                <td className="px-4 py-3"><StatusBadge value={STATUS_MAP[r.status] || r.status} label={LABEL[r.status] || r.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(r.created_at)}<p>{r.created_by}</p></td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" title="Aggiorna stato" onClick={() => act(() => api.post(`/signatures/${r.id}/refresh`), "Stato aggiornato")} data-testid={`signature-refresh-${r.id}`}><RefreshCw className="h-4 w-4" /></Button>
                  {st?.can_send && !["completed", "declined", "voided"].includes(r.status) && (
                    <Button variant="ghost" size="icon" title="Annulla" className="text-red-600" data-testid={`signature-void-${r.id}`}
                      onClick={() => { const reason = window.prompt("Motivo dell'annullamento:"); if (reason) act(() => api.post(`/signatures/${r.id}/void`, { reason }), "Richiesta annullata"); }}><XCircle className="h-4 w-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
